import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@mui/material';
import { Stack } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import PaymentProcessor, {
  AddPaymentProcessor,
} from '@/shared/types/order/paymentProcessor.type';
import SupportedPaymentProcessor from '@/shared/types/order/supportedPaymentProcessor.type';

type PaymentProcessorDialogProps = {
  title: string;
  processor?: PaymentProcessor;
  supportedPaymentProcessors: SupportedPaymentProcessor[];
  errors?: string[];
  onClose: () => void;
  proceed: (processor: AddPaymentProcessor, processorId?: string) => void;
};

const PaymentProcessorDialog: FC<PaymentProcessorDialogProps> = ({
  title,
  processor,
  supportedPaymentProcessors,
  errors,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['paymentProcessors', 'common']);

  const [formValues, setFormValues] = useState<AddPaymentProcessor>({
    processor_key: processor?.processor_key || '',
    name: processor?.name || '',
    api_key: processor?.api_key || '',
    api_pub_key: processor?.api_pub_key || '',
    api_secret: processor?.api_secret || '',
    is_test_credentials: processor?.is_test_credentials || false,
  });

  const [formErrors, setFormErrors] = useState<Partial<AddPaymentProcessor>>();

  const onValidate = () => {
    const errors: Partial<AddPaymentProcessor> = {};

    if (!formValues.processor_key) {
      errors.processor_key = t('paymentProcessors:paymentProcessorsRequired');
    }

    if (!formValues.name) {
      errors.name = t('paymentProcessors:name_required');
    }

    if (!formValues.api_key) {
      errors.api_key = t('paymentProcessors:api_key_required');
    }

    if (!formValues.api_pub_key) {
      errors.api_pub_key = t('paymentProcessors:api_pub_key_required');
    }

    if (!formValues.api_secret) {
      errors.api_secret = t('paymentProcessors:api_secret_required');
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  if (errors?.length) {
    return (
      <ModalNotification
        size='xs'
        fullWidth
        open
        onClose={onClose}
        dialogProps={{
          title,
          content: (
            <Stack spacing={2} useFlexGap>
              {t('paymentProcessors:failedToSavePaymentProcessor')}
              {errors.map((error) => (
                <Alert key={error} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ),
          actions: [
            {
              label: t('common:close'),
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title,
        content: (
          <Stack sx={{ py: 1 }}>
            <WaveUIForm_V2
              fields={[
                {
                  field_type: FieldType.Select,
                  field_props: {
                    label: t('paymentProcessors:paymentProcessors'),
                    name: 'processor_key',
                    options: supportedPaymentProcessors?.map((processor) => ({
                      label: processor.processor_name,
                      value: processor.processor_key,
                    })),
                    value: formValues.processor_key,
                    placeholder: t('paymentProcessors:selectPaymentProcessors'),
                    required: true,
                    error: !!formErrors?.processor_key,
                    helperText: formErrors?.processor_key,
                  },
                },
                {
                  field_type: FieldType.Text,
                  field_props: {
                    label: t('paymentProcessors:name'),
                    name: 'name',
                    value: formValues.name,
                    required: true,
                    placeholder: t('paymentProcessors:enter_name'),
                    error: !!formErrors?.name,
                    helperText: formErrors?.name,
                  },
                },
                {
                  field_type: FieldType.Text,
                  field_props: {
                    label: t('paymentProcessors:api_key'),
                    name: 'api_key',
                    value: formValues.api_key,
                    required: true,
                    placeholder: t('paymentProcessors:enter_api_key'),
                    multiline: true,
                    rows: 4,
                    error: !!formErrors?.api_key,
                    helperText: formErrors?.api_key,
                  },
                },
                {
                  field_type: FieldType.Text,
                  field_props: {
                    label: t('paymentProcessors:api_pub_key'),
                    name: 'api_pub_key',
                    value: formValues.api_pub_key,
                    required: true,
                    placeholder: t('paymentProcessors:enter_api_pub_key'),
                    multiline: true,
                    rows: 4,
                    error: !!formErrors?.api_pub_key,
                    helperText: formErrors?.api_pub_key,
                  },
                },
                {
                  field_type: FieldType.Text,
                  field_props: {
                    label: t('paymentProcessors:api_secret'),
                    name: 'api_secret',
                    value: formValues.api_secret,
                    required: true,
                    placeholder: t('paymentProcessors:enter_api_secret'),
                    error: !!formErrors?.api_secret,
                    helperText: formErrors?.api_secret,
                  },
                },
                {
                  field_type: FieldType.Switch,
                  field_props: {
                    label: t('paymentProcessors:is_test_credentials'),
                    name: 'is_test_credentials',
                    checked: formValues.is_test_credentials,
                    required: true,
                  },
                },
              ]}
              getFieldsValues={(value) => {
                setFormValues((prev) => ({ ...prev, ...value }));
              }}
            />
          </Stack>
        ),
        actions: [
          {
            label: t('common:cancel'),
            onClick: onClose,
          },
          {
            label: processor ? t('common:edit') : t('common:add'),
            variant: 'contained',
            onClick: () => {
              const isValid = onValidate();

              if (!isValid) {
                return;
              }

              proceed(formValues, processor?.processor_id);
            },
          },
        ],
      }}
    />
  );
};

export default PaymentProcessorDialog;
